import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from "contentful";
import type { TypeSmallCtaSkeleton } from "./TypeSmallCta";
import type { TypeTableOfContentsSettingSkeleton } from "./TypeTableOfContentsSetting";
import type { TypeTimelineItemSkeleton } from "./TypeTimelineItem";

/**
 * Fields type definition for content type 'TypeTimelineSection'
 * @name TypeTimelineSectionFields
 * @type {TypeTimelineSectionFields}
 * @memberof TypeTimelineSection
 */
export interface TypeTimelineSectionFields {
    /**
     * Field type definition for field 'internalTitle' (Internal Title)
     * @name Internal Title
     * @localized false
     */
    internalTitle?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'header' (Header)
     * @name Header
     * @localized false
     */
    header: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'subHeader' (Sub Header)
     * @name Sub Header
     * @localized false
     */
    subHeader?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'headerImage' (Header Image)
     * @name Header Image
     * @localized false
     */
    headerImage?: EntryFieldTypes.AssetLink;
    /**
     * Field type definition for field 'description' (Description)
     * @name Description
     * @localized false
     */
    description?: EntryFieldTypes.Text;
    /**
     * Field type definition for field 'timelineHeader' (Timeline Header)
     * @name Timeline Header
     * @localized false
     */
    timelineHeader?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'timelineItems' (Timeline Items)
     * @name Timeline Items
     * @localized false
     */
    timelineItems?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeTimelineItemSkeleton>>;
    /**
     * Field type definition for field 'backgroundColor' (Background Color)
     * @name Background Color
     * @localized false
     */
    backgroundColor: EntryFieldTypes.Symbol<"grey" | "light-blue" | "white">;
    /**
     * Field type definition for field 'cta1' (CTA1)
     * @name CTA1
     * @localized false
     */
    cta1?: EntryFieldTypes.EntryLink<TypeSmallCtaSkeleton>;
    /**
     * Field type definition for field 'cta2' (CTA2)
     * @name CTA2
     * @localized false
     */
    cta2?: EntryFieldTypes.EntryLink<TypeSmallCtaSkeleton>;
    /**
     * Field type definition for field 'tocSetting' (tocSetting)
     * @name tocSetting
     * @localized false
     */
    tocSetting?: EntryFieldTypes.EntryLink<TypeTableOfContentsSettingSkeleton>;
}

/**
 * Entry skeleton type definition for content type 'timelineSection' (Timeline Section)
 * @name TypeTimelineSectionSkeleton
 * @type {TypeTimelineSectionSkeleton}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-23T21:12:55.717Z
 * @version 23
 */
export type TypeTimelineSectionSkeleton = EntrySkeletonType<TypeTimelineSectionFields, "timelineSection">;
/**
 * Entry type definition for content type 'timelineSection' (Timeline Section)
 * @name TypeTimelineSection
 * @type {TypeTimelineSection}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-23T21:12:55.717Z
 * @version 23
 */
export type TypeTimelineSection<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<TypeTimelineSectionSkeleton, Modifiers, Locales>;

export function isTypeTimelineSection<Modifiers extends ChainModifiers, Locales extends LocaleCode>(entry: Entry<EntrySkeletonType, Modifiers, Locales>): entry is TypeTimelineSection<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'timelineSection'
}
